// LearnCore Frontend - 任务列表组件

import React, { useState, useEffect } from 'react';
import { tasksApi } from '../api/tasks';
import TaskCard from './TaskCard';
import type { 
  UserTask, 
  TaskStatus, 
  TaskType, 
  TaskCategory, 
  TaskPriority,
  GetTasksRequest 
} from '../types/tasks-rewards';

interface TaskListProps {
  tasks?: UserTask[]; // 新增：直接传入任务列表
  userId?: string;
  filters?: {
    status?: TaskStatus[];
    type?: TaskType[];
    category?: TaskCategory[];
    priority?: TaskPriority[];
  };
  sortBy?: 'created_at' | 'due_date' | 'priority' | 'dkp_reward';
  sortOrder?: 'asc' | 'desc';
  showActions?: boolean;
  compact?: boolean;
  onTaskUpdate?: (task: UserTask) => void;
  onTaskDelete?: (taskId: string) => void;
  className?: string;
}

export const TaskList: React.FC<TaskListProps> = ({
  tasks: externalTasks,
  userId,
  filters,
  sortBy = 'created_at',
  sortOrder = 'desc',
  showActions = true,
  compact = false,
  onTaskUpdate,
  onTaskDelete,
  className = ''
}) => {
  const [tasks, setTasks] = useState<UserTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);

  // 如果传入了tasks，使用传入的数据；否则从API获取
  const useExternalData = externalTasks !== undefined;

  const loadTasks = async (reset = false) => {
    if (useExternalData) {
      // 使用外部传入的数据
      setTasks(externalTasks || []);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const request: GetTasksRequest = {
        user_id: userId,
        status: filters?.status,
        type: filters?.type,
        category: filters?.category,
        priority: filters?.priority,
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: 20,
        offset: reset ? 0 : page * 20
      };

      const response = await tasksApi.getTasks(request);

      if (reset) {
        setTasks(response.tasks || []);
        setPage(0);
      } else {
        setTasks(prev => [...prev, ...(response.tasks || [])]);
      }

      setHasMore(response.has_more);
    } catch (err) {
      setError('加载任务失败');
      console.error('Failed to load tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!useExternalData) {
      loadTasks(true);
    }
  }, [userId, filters, sortBy, sortOrder, externalTasks, useExternalData]);

  // 当外部数据变化时更新
  useEffect(() => {
    if (useExternalData) {
      setTasks(externalTasks || []);
      setLoading(false);
    }
  }, [externalTasks, useExternalData]);

  const handleComplete = async (taskId: string) => {
    try {
      const response = await tasksApi.completeTask(taskId, {
        completion_notes: '用户手动完成'
      });
      
      setTasks(prev => (prev || []).map(task =>
        task.id === taskId ? response.task : task
      ));
      
      if (onTaskUpdate) {
        onTaskUpdate(response.task);
      }
    } catch (err) {
      console.error('Failed to complete task:', err);
      setError('完成任务失败');
    }
  };

  const handleUpdate = async (taskId: string, updates: Partial<UserTask>) => {
    try {
      const response = await tasksApi.updateTask(taskId, updates);
      
      setTasks(prev => (prev || []).map(task =>
        task.id === taskId ? response.task : task
      ));
      
      if (onTaskUpdate) {
        onTaskUpdate(response.task);
      }
    } catch (err) {
      console.error('Failed to update task:', err);
      setError('更新任务失败');
    }
  };

  const handleDelete = async (taskId: string) => {
    try {
      await tasksApi.deleteTask(taskId);
      
      setTasks(prev => (prev || []).filter(task => task.id !== taskId));
      
      if (onTaskDelete) {
        onTaskDelete(taskId);
      }
    } catch (err) {
      console.error('Failed to delete task:', err);
      setError('删除任务失败');
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
      loadTasks(false);
    }
  };

  const getEmptyMessage = () => {
    if (filters?.status && filters.status.length === 1) {
      const status = filters.status[0];
      switch (status) {
        case 'pending': return '暂无待开始的任务';
        case 'in_progress': return '暂无进行中的任务';
        case 'completed': return '暂无已完成的任务';
        case 'expired': return '暂无已过期的任务';
      }
    }
    return '暂无任务';
  };

  if (loading && tasks.length === 0) {
    return (
      <div className={`space-y-4 ${className}`}>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2 mb-3"></div>
              <div className="h-2 bg-gray-200 rounded w-full mb-1"></div>
              <div className="h-2 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <div className="text-red-800">{error}</div>
        <button
          onClick={() => loadTasks(true)}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          重试
        </button>
      </div>
    );
  }

  return (
    <div className={className}>
      {!tasks || tasks.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="text-gray-400 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <div className="text-gray-600 font-medium">{getEmptyMessage()}</div>
          <div className="text-gray-500 text-sm mt-1">创建你的第一个任务开始学习吧！</div>
        </div>
      ) : (
        <>
          <div className={`space-y-4 ${compact ? 'space-y-2' : ''}`}>
            {tasks && tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onComplete={handleComplete}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                showActions={showActions}
                compact={compact}
              />
            ))}
          </div>

          {hasMore && (
            <div className="text-center mt-6">
              <button
                onClick={loadMore}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
              >
                {loading ? '加载中...' : '加载更多'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TaskList;